// @flow
import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import * as linkHeaderParser from 'linkheader-parser/dist/linkheader-parser-node';
import ProjectClient from '../../models/projects/ProjectClient';
import Loader from '../common/Loader';
import Card from './Card';
import type { Category } from '../../models/categories/Category';
import type { Project } from '../../models/projects/Project';

const ORDERS = [
  { order: 'in_progress', name: '募集中のプロジェクト' },
  { order: 'amount', name: '支援金額が多いプロジェクト' },
  { order: 'succeeded', name: '成約したプロジェクト' },
];

type Props = {
  categories: Array<Category>,
  keyword: ?string,
  order: ?string,
  mobile: boolean,
  spefic_investor_flg: boolean,
};

type State = {
  projects: Array<Project>,
  keyword: string,
  order: ?string,
  category: ?number,
  isLoading: boolean,
  page: number,
  hasNextPage: boolean,
  typingTimeout: any,
  isOpenCategory: boolean,
};

export default class IndexView extends React.Component<Props, State> {
  projectClient = new ProjectClient();

  constructor(props: Props) {
    super(props);

    this.state = {
      projects: [],
      keyword: props.keyword != null ? props.keyword : '',
      order: props.order,
      spefic_investor_flg: props.spefic_investor_flg,
      category: null,
      isLoading: true,
      page: 1,
      hasNextPage: true,
      typingTimeout: 0,
      isOpenCategory: false,
    };
  }

  componentDidMount() {
    this.fetchProjects();
  }

  fetchProjects() {
    const { page, keyword, order, category } = this.state;

    this.projectClient
      .fetchAll(page, keyword === '' ? null : keyword, order, category)
      .then(response => {
        const hasNextPage =
          response.headers &&
          response.headers.hasOwnProperty('link') &&
          linkHeaderParser.parse(response.headers.link).hasOwnProperty('next');
        this.setState({
          projects: [...this.state.projects, ...response.data],
          hasNextPage: hasNextPage,
          isLoading: false,
        });
      })
      .catch(error => {
        console.warn(error);
      });
  }

  onLoadMore() {
    if (!this.state.isLoading && this.state.hasNextPage) {
      this.setState(
        {
          page: this.state.page + 1,
          isLoading: true,
        },
        () => this.fetchProjects(),
      );
    }
  }

  onChangeKeyword(keyword: string) {
    if (this.state.typingTimeout > 0) {
      clearTimeout(this.state.typingTimeout);
    }

    this.setState({
      projects: [],
      page: 1,
      isLoading: true,
      hasNextPage: true,
      keyword: keyword,
      order: null,
      category: null,
      typingTimeout: setTimeout(() => {
        this.fetchProjects();
        // history.pushState(null, '', `/projects?keyword=${keyword}`);
      }, 1000),
    });
  }

  onClickOrder(order: ?string) {
    if (!this.state.isLoading) {
      this.setState(
        {
          projects: [],
          page: 1,
          isLoading: true,
          hasNextPage: true,
          keyword: '',
          order: order,
          category: null,
        },
        () => this.fetchProjects(),
      );
    }
  }

  onClickCategory(category: number) {
    if (!this.state.isLoading) {
      this.setState(
        {
          projects: [],
          page: 1,
          isLoading: true,
          hasNextPage: true,
          keyword: '',
          order: null,
          category: category,
        },
        () => this.fetchProjects(),
      );
    }
  }

  render() {
    const { categories, spefic_investor_flg } = this.props;
    const { keyword, isLoading } = this.state;
    const projects = this.state.projects.map(project => (
      <Card
        key={project.id}
        project={project}
        spefic_investor_flg={spefic_investor_flg}
      />
    ));

    return (
      <div>
        <div className="clearfix mb-40">
          <div className="float-md-left">
            <h1>プロジェクトを探す</h1>
          </div>
          <div className="float-md-right">
            <div className="search-wrapper">
              <form
                acceptCharset="UTF-8"
                method="get"
                onSubmit={e => e.preventDefault()}
              >
                <div className="input-group">
                  <div className="input-group-addon">
                    <i className="ion-ios-search" />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    name="keyword"
                    maxLength="50"
                    value={keyword}
                    onChange={e => this.onChangeKeyword(e.target.value)}
                    placeholder="キーワードで検索"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-3">
            <div className="list-group mb-30">
              <a
                className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                  this.state.order == null && this.state.category == null
                    ? 'active'
                    : ''
                }`}
                onClick={() => this.onClickOrder(null)}
              >
                すべて
              </a>
              {ORDERS.map(order => (
                <a
                  key={order.order}
                  className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                    this.state.order === order.order ? 'active' : ''
                  }`}
                  onClick={() => this.onClickOrder(order.order)}
                >
                  {order.name}
                </a>
              ))}
            </div>
            <div className="list-group mb-30">
              <div>
                <a
                  className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                  onClick={() =>
                    this.setState({
                      isOpenCategory: !this.state.isOpenCategory,
                    })
                  }
                >
                  カテゴリから探す
                  <i className="fas fa-angle-down float-right" />
                </a>
                {this.state.isOpenCategory &&
                  categories.map(category => (
                    <a
                      key={category.id}
                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                        this.state.category === category.id ? 'active' : ''
                      }`}
                      onClick={() => this.onClickCategory(category.id)}
                    >
                      {category.name}
                      <i className="fas fa-angle-right" />
                    </a>
                  ))}
              </div>
            </div>

            <div className="alert alert-primary p-2">
              ※
              募集ページについては、募集期間終了後、募集の成立不成立に関わらず事業者の要請に応じて非開示となることがあります。ご了承下さい。
            </div>
          </div>
          <div className="col-lg-9">
            <InfiniteScroll
              pageStart={0}
              loadMore={() => this.onLoadMore()}
              hasMore={this.state.hasNextPage}
              loader={<Loader key={0} type="water" />}
              className="row"
            >
              {projects}
            </InfiniteScroll>
            {!isLoading && projects.length === 0 && (
              <div>プロジェクトは見つかりませんでした</div>
            )}
          </div>
        </div>
      </div>
    );
  }
}
